import { useTranslation } from "react-i18next";

import {
  getFocusedItemId,
  type HaNFloorSuggestion,
  HaNViewStoreContext,
  selectSelection,
  useHaNView,
} from "@lib/logic";
import type { FacilityResource } from "@lib/store";
import { useRequiredContext } from "@lib/utils";
import { EmptyListPlaceholder } from "@molecules";

import { useFitNodeInView } from "../map/useFitNodeInView";
import { FloorSuggestionTile } from "../shared/FloorSuggestionTile";
import { HaNCellSkeleton } from "../shared/HaNCellSkeleton";
import { HaNSlider } from "../shared/HaNSlider/HaNSlider";
import { useOpenResult } from "../shared/useOpenResult";
import { useOpenSuggestedResult } from "../shared/useOpenSuggestedResult";
import { HaNContext } from "../shell/HaNContext";
import { FacilityCell } from "./FacilityCell";
import { useFacilitiesModel } from "./useFacilitiesModel";

export const FacilitiesCarousel = () => {
  const { tab } = useRequiredContext(HaNContext);

  if (tab !== "facilities") return null;

  return <FacilitiesCarouselContent />;
};

// The same facilities as the list, then each other floor's tile and its suggestions. It centres on the selected
// facility, or the first one.
const FacilitiesCarouselContent = () => {
  const { t } = useTranslation();
  const { isLoading, facilities, suggestions } = useFacilitiesModel();
  const viewStore = useRequiredContext(HaNViewStoreContext);
  const selectedId = useHaNView(state => selectSelection("facilities")(state)?.id ?? null);
  const openResult = useOpenResult();
  const openSuggestedResult = useOpenSuggestedResult();
  const fitNodeInView = useFitNodeInView();

  if (isLoading) {
    return (
      <HaNSlider.Card className="items-center">
        <HaNCellSkeleton />
      </HaNSlider.Card>
    );
  }

  if (facilities.length === 0 && !suggestions.above && !suggestions.below) {
    return (
      <HaNSlider.Card>
        <EmptyListPlaceholder text={t("hereAndNow:NoFacilitiesAvailable")} />
      </HaNSlider.Card>
    );
  }

  const facilityIds = facilities.map(f => f.id);
  const focusedIndex = facilityIds.indexOf(getFocusedItemId(facilityIds, selectedId) ?? "");

  // Swiping to one of the floor's facilities selects it, like tapping it on the map. Other floors aren't on the map.
  const focusSlide = (index: number) => {
    const facility = facilities[index];

    if (!facility) return;

    viewStore.select("facilities", { kind: "facility", id: facility.id });

    fitNodeInView(facility.id);
  };

  // Swiper only finds slides among its children and fragments, so these are elements rather than a component
  const renderSuggestionSlides = (
    suggestion: HaNFloorSuggestion<FacilityResource> | null,
    direction: "above" | "below",
  ) => {
    if (!suggestion) return null;

    return (
      <>
        <HaNSlider.Slide>
          <HaNSlider.Card>
            <FloorSuggestionTile
              floorId={suggestion.floorId}
              direction={direction}
            />
          </HaNSlider.Card>
        </HaNSlider.Slide>

        {suggestion.items.map(facility => (
          <HaNSlider.Slide key={facility.id}>
            <HaNSlider.CardButton onClick={() => openSuggestedResult("facility", facility, direction)}>
              <FacilityCell facility={facility} />
            </HaNSlider.CardButton>
          </HaNSlider.Slide>
        ))}
      </>
    );
  };

  return (
    <HaNSlider
      focusedIndex={focusedIndex}
      onFocusChange={focusSlide}>
      {facilities.length > 0 ? (
        facilities.map(facility => (
          <HaNSlider.Slide key={facility.id}>
            <HaNSlider.CardButton onClick={() => openResult("facility", facility)}>
              <FacilityCell facility={facility} />
            </HaNSlider.CardButton>
          </HaNSlider.Slide>
        ))
      ) : (
        <HaNSlider.Slide>
          <HaNSlider.Card>
            <EmptyListPlaceholder
              text={t("hereAndNow:NoFacilitiesAvailable")}
              className="justify-center p-2 [&>svg]:size-14"
            />
          </HaNSlider.Card>
        </HaNSlider.Slide>
      )}
      {renderSuggestionSlides(suggestions.above, "above")}
      {renderSuggestionSlides(suggestions.below, "below")}
    </HaNSlider>
  );
};
